import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, Phone, MessageSquare, User, CheckCircle, Clock, XCircle, Download, Trash2, AlertTriangle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSubmissions = () => {
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [volunteerApplications, setVolunteerApplications] = useState([]);
  const [newsletterSubscriptions, setNewsletterSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearType, setClearType] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSubmissions();
  }, [navigate]);

  const fetchSubmissions = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const [contactRes, volunteerRes, newsletterRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/submissions/contact`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/submissions/volunteer`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/submissions/newsletter`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setContactSubmissions(contactRes.data);
      setVolunteerApplications(volunteerRes.data);
      setNewsletterSubscriptions(newsletterRes.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch submissions",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateContactStatus = async (submissionId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${BACKEND_URL}/api/submissions/contact/${submissionId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Success", description: "Status updated successfully" });
      fetchSubmissions();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const updateVolunteerStatus = async (applicationId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${BACKEND_URL}/api/submissions/volunteer/${applicationId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Success", description: "Status updated successfully" });
      fetchSubmissions();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const handleExport = async (type) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/submissions/${type}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_submissions.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({ title: "Success", description: `${type.charAt(0).toUpperCase() + type.slice(1)} data exported successfully` });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export data",
        variant: "destructive"
      });
    }
  };

  const handleClearConfirm = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/submissions/${clearType}/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: `${clearType.charAt(0).toUpperCase() + clearType.slice(1)} submissions cleared successfully` });
      setClearDialogOpen(false);
      setClearType(null);
      fetchSubmissions();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to clear submissions",
        variant: "destructive"
      });
    }
  };

  const openClearDialog = (type) => {
    setClearType(type);
    setClearDialogOpen(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'read':
      case 'approved':
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'replied':
        return <MessageSquare className="w-4 h-4 text-blue-600" />;
      case 'rejected':
      case 'unsubscribed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'read':
      case 'approved':
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'replied':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
      case 'unsubscribed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center space-x-4 mb-8">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="outline"
            className="border-[#007A7A] text-[#007A7A]"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Form Submissions</h1>
        </div>

        <Tabs defaultValue="contact" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="contact" data-testid="contact-tab">
              Contact ({contactSubmissions.length})
            </TabsTrigger>
            <TabsTrigger value="volunteer" data-testid="volunteer-tab">
              Volunteer ({volunteerApplications.length})
            </TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="newsletter-tab">
              Newsletter ({newsletterSubscriptions.length})
            </TabsTrigger>
          </TabsList>

          {/* Contact Tab */}
          <TabsContent value="contact">
            <div className="flex justify-end gap-3 mb-4">
              <Button
                onClick={() => handleExport('contact')}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="export-contact-btn"
                disabled={contactSubmissions.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={() => openClearDialog('contact')}
                variant="outline"
                className="border-red-600 text-red-600 hover:bg-red-50"
                data-testid="clear-contact-btn"
                disabled={contactSubmissions.length === 0}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            <div className="space-y-4" data-testid="contact-submissions-list">
              {contactSubmissions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No contact submissions yet.
                </div>
              ) : (
                contactSubmissions.map((submission) => (
                  <Card key={submission.id} className="hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{submission.subject}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(submission.status)}`}>
                              {getStatusIcon(submission.status)}
                              {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                            <span className="flex items-center">
                              <User className="w-4 h-4 mr-1" />
                              {submission.name}
                            </span>
                            <span className="flex items-center">
                              <Mail className="w-4 h-4 mr-1" />
                              <a href={`mailto:${submission.email}`} className="text-blue-600 hover:underline">
                                {submission.email}
                              </a>
                            </span>
                            {submission.phone && (
                              <span className="flex items-center">
                                <Phone className="w-4 h-4 mr-1" />
                                {submission.phone}
                              </span>
                            )}
                            <span className="text-gray-400">
                              {new Date(submission.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{submission.message}</p>
                        </div>
                        <div className="flex flex-col space-y-2 ml-4">
                          {submission.status === 'new' && (
                            <Button
                              size="sm"
                              onClick={() => updateContactStatus(submission.id, 'read')}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`mark-read-${submission.id}`}
                            >
                              Mark Read
                            </Button>
                          )}
                          {submission.status === 'read' && (
                            <Button
                              size="sm"
                              onClick={() => updateContactStatus(submission.id, 'replied')}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid={`mark-replied-${submission.id}`}
                            >
                              Mark Replied
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Volunteer Tab */}
          <TabsContent value="volunteer">
            <div className="flex justify-end gap-3 mb-4">
              <Button
                onClick={() => handleExport('volunteer')}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="export-volunteer-btn"
                disabled={volunteerApplications.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={() => openClearDialog('volunteer')}
                variant="outline"
                className="border-red-600 text-red-600 hover:bg-red-50"
                data-testid="clear-volunteer-btn"
                disabled={volunteerApplications.length === 0}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            <div className="space-y-4" data-testid="volunteer-applications-list">
              {volunteerApplications.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No volunteer applications yet.
                </div>
              ) : (
                volunteerApplications.map((application) => (
                  <Card key={application.id} className="hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{application.name}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(application.status)}`}>
                              {getStatusIcon(application.status)}
                              {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                            <span className="flex items-center">
                              <Mail className="w-4 h-4 mr-1" />
                              <a href={`mailto:${application.email}`} className="text-blue-600 hover:underline">
                                {application.email}
                              </a>
                            </span>
                            <span className="flex items-center">
                              <Phone className="w-4 h-4 mr-1" />
                              {application.phone}
                            </span>
                            <span className="text-gray-400">
                              {new Date(application.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="mb-3">
                            <span className="text-sm font-medium text-gray-700">Availability: </span>
                            <span className="text-sm text-gray-600">{application.availability}</span>
                          </div>
                          <div className="mb-3">
                            <span className="text-sm font-medium text-gray-700">Interests: </span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {application.interests.map((interest, idx) => (
                                <span key={`interest-${interest}`} className="bg-[#007A7A]/10 text-[#007A7A] px-2 py-1 rounded-full text-xs">
                                  {interest}
                                </span>
                              ))}
                            </div>
                          </div>
                          {application.message && (
                            <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{application.message}</p>
                          )}
                        </div>
                        <div className="flex flex-col space-y-2 ml-4">
                          {application.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => updateVolunteerStatus(application.id, 'approved')}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`approve-${application.id}`}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateVolunteerStatus(application.id, 'rejected')}
                                className="border-red-600 text-red-600 hover:bg-red-50"
                                data-testid={`reject-${application.id}`}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Newsletter Tab */}
          <TabsContent value="newsletter">
            <div className="flex justify-end gap-3 mb-4">
              <Button
                onClick={() => handleExport('newsletter')}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="export-newsletter-btn"
                disabled={newsletterSubscriptions.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={() => openClearDialog('newsletter')}
                variant="outline"
                className="border-red-600 text-red-600 hover:bg-red-50"
                data-testid="clear-newsletter-btn"
                disabled={newsletterSubscriptions.length === 0}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
            <div className="space-y-4" data-testid="newsletter-subscriptions-list">
              {newsletterSubscriptions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No newsletter subscriptions yet.
                </div>
              ) : (
                <Card>
                  <CardContent className="p-6">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Email</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Subscribed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {newsletterSubscriptions.map((sub) => (
                            <tr key={sub.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <a href={`mailto:${sub.email}`} className="text-blue-600 hover:underline">
                                  {sub.email}
                                </a>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${getStatusColor(sub.status)}`}>
                                  {getStatusIcon(sub.status)}
                                  {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-gray-500">
                                {new Date(sub.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Clear Confirmation Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Clear All
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to clear all {clearType} submissions? This action cannot be undone. 
              Make sure you've exported the data first if you need to keep a record.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setClearDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleClearConfirm}
              data-testid="confirm-clear-btn"
            >
              Yes, Clear All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubmissions;
